import Close from '@mui/icons-material/Close'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { getZipCode } from '@returnmates/client-core/src/selectors/user'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import validate from '@returnmates/client-core/src/utils/validation/signIn'
import Button from '@returnmates/ui-core/src/components/Button'
import Eye from '@returnmates/ui-core/src/components/images/icons/eye'
import EyeClose from '@returnmates/ui-core/src/components/images/icons/eyeClose'
import Logo from '@returnmates/ui-core/src/components/images/logo'
import Input from '@returnmates/ui-core/src/components/Input'
import clsx from 'clsx'
import qs from 'query-string'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import Checkbox from '../../components/Checkbox'
import ArrowLeft from '../../components/images/arrowLeft'
import LeftPartWithImages from '../../components/LeftPartWithImages'
import { FIRST_PICKUP, LOGIN, PRIVACY, TERMS, ZIP_CODE } from '../../constants/paths'
import { MAIN } from '../../constants/paths'
import useStyles from './styles'

function Registration() {
  const classes = useStyles()
  const zipCodeProp = useSelector(getZipCode)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)

  const zipCode = useMemo<number>(() => zipCodeProp || Number(qs.parse(location.search).zipCode), [
    zipCodeProp,
    location.search,
  ])

  const onSubmit = useCallback(
    async ({ password, email }: { password: string; email: string }) => {
      try {
        setIsLoading(true)
        await createAsyncAction(dispatch, actions.signUp.request({ username: email, password }))

        setIsLoading(false)
        navigate(FIRST_PICKUP)
      } catch (err) {
        setIsLoading(false)
        const { message } = err as Error
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      }
    },
    [dispatch, navigate],
  )

  const switchPasswordVisibility = useCallback(() => {
    setIsPasswordVisible(val => !val)
  }, [])

  const goBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  useEffect(() => {
    if (!zipCode) {
      navigate(ZIP_CODE)
    }
  }, [zipCode, navigate])

  return (
    <div className={classes.main}>
      <LeftPartWithImages />
      <div className={classes.mainPart}>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          render={({ handleSubmit }) => (
            <>
              <div className={classes.signIn}>
                Already have an account?{' '}
                <Link to={LOGIN} className={classes.link}>
                  Sign in
                </Link>
              </div>
              <Link to={MAIN}>
                <Logo className={classes.logo} />
              </Link>
              <h1 className={classes.header}>
                Get rid of your
                <br />
                returns in <span>minutes</span>
              </h1>
              <span className={classes.mobileLabel}>
                With just a few clicks you can sign up and schedule your first return
              </span>
              <Field name="email">
                {({ input, meta }) => (
                  <form onSubmit={handleSubmit} className={classes.form}>
                    <Input
                      onChange={input.onChange}
                      value={input.value}
                      error={meta.touched && meta.error}
                      helperText={meta.error}
                      placeholder="Email"
                      className={clsx(classes.emailInput, classes.input)}
                      autoFocus
                    />
                  </form>
                )}
              </Field>
              <div className={classes.passwordInputWrapper}>
                <Field name="password">
                  {({ input, meta }) => (
                    <form onSubmit={handleSubmit}>
                      <Input
                        onChange={input.onChange}
                        value={input.value}
                        error={meta.touched && meta.error}
                        helperText={meta.error}
                        placeholder="Password"
                        className={clsx(classes.passwordInput, classes.input)}
                        type={isPasswordVisible ? 'input' : 'password'}
                      />
                    </form>
                  )}
                </Field>
                {isPasswordVisible ? (
                  <Eye className={classes.eyeIcon} onClick={switchPasswordVisibility} />
                ) : (
                  <EyeClose className={classes.eyeIcon} onClick={switchPasswordVisibility} />
                )}
              </div>
              <Field name="termsAndConditions">
                {({ input, meta }) => (
                  <div className={classes.termsAndPrivacy}>
                    <>
                      <Checkbox
                        onChange={input.onChange}
                        checked={input.value}
                        className={classes.termsCheckbox}
                      />
                    </>
                    <p className={classes.termsAndPrivacyText}>
                      I agree to the <Link to={TERMS}>Terms of Service</Link> and{' '}
                      <Link to={PRIVACY}>Privacy Policy</Link>
                    </p>
                    {meta.touched && meta.error ? (
                      <p className={classes.error}>{meta.error}</p>
                    ) : null}
                  </div>
                )}
              </Field>
              <Button
                fullWidth
                onClick={handleSubmit}
                label="Let’s go!"
                className={classes.button}
                isLoading={isLoading}
              />
              <div className={classes.mobileHeader}>
                <div className={classes.back} onClick={goBack}>
                  <ArrowLeft />
                  <span>Back</span>
                </div>
                <Link to={MAIN}>
                  <Close className={classes.exitIcon} />
                </Link>
              </div>
            </>
          )}
        />
      </div>
    </div>
  )
}

export default memo(Registration)
